import { useRef } from 'react';

/**
 * Reusable camera/photo capture component for rebus tasks.
 * Uses native file input with camera capture for mobile devices.
 *
 * @param {Object} props
 * @param {function} props.onCapture - Callback with captured File
 * @param {string} [props.preview] - Existing image URL to show as preview
 * @param {function} [props.onClear] - Callback to clear the current photo
 */
export default function PhotoCapture({ onCapture, preview, onClear }) {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onCapture(file);
    }
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
        aria-label="Ta bilete"
      />

      {preview ? (
        /* Image preview */
        <div className="relative rounded-2xl overflow-hidden border-2 border-teal-300 bg-gray-900">
          <img
            src={preview}
            alt="Opplasta bilete"
            className="w-full h-48 object-cover"
          />
          <div className="absolute bottom-0 inset-x-0 flex gap-2 p-3 bg-gradient-to-t from-black/70 to-transparent">
            <button
              type="button"
              onClick={handleButtonClick}
              className="flex-1 flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm text-white font-semibold py-2.5 rounded-xl active:scale-95 transition-transform"
            >
              <span className="material-symbols-outlined text-xl">photo_camera</span>
              <span>Nytt bilete</span>
            </button>
            {onClear && (
              <button
                type="button"
                onClick={onClear}
                className="flex items-center justify-center gap-1 bg-red-500/80 backdrop-blur-sm text-white font-semibold px-4 py-2.5 rounded-xl active:scale-95 transition-transform"
              >
                <span className="material-symbols-outlined text-xl">close</span>
                <span>Fjern</span>
              </button>
            )}
          </div>
        </div>
      ) : (
        /* Capture button */
        <button
          type="button"
          onClick={handleButtonClick}
          className="w-full flex flex-col items-center justify-center gap-3 py-8 bg-teal-50 border-2 border-dashed border-teal-300 rounded-2xl text-teal-700 active:bg-teal-100 active:scale-[0.98] transition-all"
        >
          <span className="material-symbols-outlined text-4xl">photo_camera</span>
          <span className="font-semibold text-lg">Ta bilete</span>
        </button>
      )}
    </div>
  );
}
